// Internal Brands page card strip (TDC Consumer Voice / Brand Intelligence):
// one line per report type — PDP, CV, AIP (AI Presence), BI — each with its
// score (PDP / CV / AIP),
// view / download of the latest report, and a run icon, mirroring the
// product card's per-URL icon rows. Internal users act as the brand's own
// root (?rmn_brand_id=id) for the PDP report, exactly like the network
// Catalog > Brands page.
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { Close as CloseIcon, FileDownloadOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import {
  adminBrandAiPresenceEndpoint,
  adminBrandAiPresenceItemEndpoint,
  adminBrandAiPresenceTargetsEndpoint,
  adminBrandConsumerVoiceEndpoint,
  adminBrandConsumerVoiceItemEndpoint,
  adminBrandConsumerVoiceTargetsEndpoint,
  adminBrandPrepPacksEndpoint,
  salesPrepPackEndpoint,
} from '../../../api/endpoints';
import { BrandReportForm } from '../../../components/PdpAudit/BrandReportForm';
import { BrandCvRunForm } from '../../../components/ConsumerVoice/BrandCvRunForm';
import { BrandAipRunForm } from '../../../components/AiPresence/BrandAipRunForm';
import { AipScoreChip } from '../../../components/AiPresence/AipScoreChip';
import { AiPresenceReportDialog } from '../../../components/AiPresence/AiPresenceReportDialog';
import { downloadAiPresencePdf } from '../../../components/AiPresence/aiPresenceFiles';
import { BrandIntelligenceRunForm } from '../../../components/PrepPack/BrandIntelligenceRunForm';
import { ScoreChip } from '../../network/catalogShared';
import { CvScoreChip } from '../../../components/ConsumerVoice/CvScoreChip';
import { ConsumerVoiceReportDialog } from '../../../components/ConsumerVoice/ConsumerVoiceReportDialog';
import { downloadConsumerVoicePdf } from '../../../components/ConsumerVoice/consumerVoiceFiles';
import { downloadPrepPackById } from '../../../components/PrepPack/prepPackActions';
import { downloadRmnReportById } from '../../../components/PdpAudit/reportFiles';
import { ReportViewerDialog } from '../../../components/PdpAudit/ReportViewerDialog';
import { IconRefresh } from '../../../utils/icons';
import { PrepPackStatusBadge } from '../../../components/PrepPack/PrepPackList';
import { RunningBadge } from '../../../components/RunningBadge';
import { useStrategyFromAnalysis } from '../../../components/ProposalFlow/useStrategyFromAnalysis';

export interface BrandIntelRow {
  id: number;
  name: string;
  pdp_score?: number | null;
  latest_pdp_report?: { id: number; rmn_brand_id: number | null; created_at: string } | null;
  // A brand PDP report still generating (server flag, like cv_running).
  pdp_report_running?: boolean;
  cv_score?: number | null;
  cv_scored_product_count?: number;
  cv_latest_report?: { id: number; overall_score: number | null; created_at: string } | null;
  cv_running?: boolean;
  aip_score?: number | null;
  aip_scored_product_count?: number;
  aip_latest_report?: { id: number; overall_score: number | null; created_at: string } | null;
  aip_running?: boolean;
  latest_prep_pack?: { id: number; status: 'generating' | 'ready' | 'error'; created_at: string } | null;
  prep_pack_running?: boolean;
}

const icon = 'w-5 h-5 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0';
const PICKER_TITLES: Record<'pdp' | 'cv' | 'aip' | 'bi', string> = {
  pdp: 'Brand PDP Analysis',
  cv: 'Brand CV Analysis',
  aip: 'Brand AI Presence Analysis',
  bi: 'Sales Intel Prep Pack',
};
const runIcon = `${icon} disabled:opacity-40 disabled:cursor-not-allowed`;

export function BrandIntelFooter({ brand, onChanged }: { brand: BrandIntelRow; onChanged?: () => void }) {
  // Optimistic running flags: the card spins the moment a run is accepted,
  // before the brands list refetch confirms it from the server.
  const [pdpRunning, setPdpRunning] = useState(false);
  const [cvRunning, setCvRunning] = useState(false);
  const [aipRunning, setAipRunning] = useState(false);
  const [viewingAip, setViewingAip] = useState<number | null>(null);
  const [biRunning, setBiRunning] = useState(false);
  const [viewingPdp, setViewingPdp] = useState<number | null>(null);
  const [viewingCv, setViewingCv] = useState<number | null>(null);
  // Every list reload hands the card a fresh brand object carrying the
  // server's running flags — from then on they are the truth, so the
  // optimistic flags drop (otherwise a run icon would stay disabled until
  // the page was reloaded).
  useEffect(() => {
    setPdpRunning(false);
    setCvRunning(false);
    setAipRunning(false);
    setBiRunning(false);
  }, [brand]);

  // PDP and CV runs open a picker (the same forms the network Catalog >
  // Brands page uses) so the user chooses the products; a brand with none
  // is sent to add one. Internal users act as the brand's own root.
  const navigate = useNavigate();
  const createStrategy = useStrategyFromAnalysis();
  const [picker, setPicker] = useState<'pdp' | 'cv' | 'aip' | 'bi' | null>(null);
  const addProduct = { label: 'Add a product', onClick: () => navigate(`/products/${brand.id}`) };
  const runPdp = () => setPicker('pdp');
  const runCv = () => setPicker('cv');

  const runBi = () => setPicker('bi');

  // One report type per line: label, score (when the type has one), then
  // view / download of the latest report (dated in the tooltip), then run.
  const line = ({
    label,
    score,
    latest,
    view,
    download,
    run: runFn,
    running,
    runTitle,
    status,
    canDownload = true,
  }: {
    label: string;
    score?: React.ReactNode;
    // BI has no score; its slot shows the pack status badge instead.
    status?: React.ReactNode;
    latest: { created_at: string } | null | undefined;
    view: (() => void) | null;
    download: () => void;
    run: () => void;
    running: boolean;
    runTitle: string;
    // Default true; a generating / failed BI pack has nothing to download.
    canDownload?: boolean;
  }) => {
    const dated = latest ? ` (${dayjs(latest.created_at).format('MMM DD, YYYY')})` : '';
    return (
      <div className="flex items-center gap-1 text-[11px] text-muted min-w-0">
        <span className="w-[28px] font-bold shrink-0">{label}</span>
        {score !== undefined && (score ?? <span>—</span>)}
        {status}
        {latest && view && (
          <Tooltip title={`View latest ${label} report${dated}`}>
            <button type="button" className={icon} onClick={view} aria-label={`View ${label} report`}>
              <VisibilityOutlined sx={{ fontSize: 14 }} />
            </button>
          </Tooltip>
        )}
        {latest && canDownload && (
          <Tooltip title={`Download latest ${label} report${dated}`}>
            <button type="button" className={icon} onClick={download} aria-label={`Download ${label} report`}>
              <FileDownloadOutlined sx={{ fontSize: 14 }} />
            </button>
          </Tooltip>
        )}
        {/* In flight: the run icon becomes a spinner badge, so PDP and CV
            runs read the same way the BI status badge does. BI already
            shows its own Generating badge in the status slot, so it keeps
            the (disabled) icon. */}
        {running && !status ? (
          <RunningBadge label="Analyzing" title={`${label} analysis running — the result lands here when it finishes.`} />
        ) : (
          <Tooltip title={running ? `${label} analysis running — the result lands here when it finishes.` : runTitle}>
            {/* span: MUI Tooltip needs a hoverable child even when disabled */}
            <span className="inline-flex">
              <button
                type="button"
                className={runIcon}
                onClick={runFn}
                disabled={running}
                aria-label={`Run ${label} analysis`}
              >
                <IconRefresh size={13} />
              </button>
            </span>
          </Tooltip>
        )}
      </div>
    );
  };

  // Same badge as Sales > Brand Intelligence's status column — a pack in
  // flight spins here exactly as it does there.
  let biStatus: React.ReactNode = <span>—</span>;
  if (brand.prep_pack_running || biRunning) {
    biStatus = <PrepPackStatusBadge pack={{ status: 'generating' }} regenerating={false} internal={false} />;
  } else if (brand.latest_prep_pack) {
    biStatus = <PrepPackStatusBadge pack={brand.latest_prep_pack} regenerating={false} internal={false} />;
  }

  return (
    <div className="flex flex-col gap-1">
      {line({
        label: 'PDP',
        score: typeof brand.pdp_score === 'number' ? <ScoreChip score={brand.pdp_score} /> : null,
        latest: brand.latest_pdp_report,
        view: () => setViewingPdp(brand.latest_pdp_report!.id),
        download: () => downloadRmnReportById(brand.latest_pdp_report!.id, brand.latest_pdp_report!.rmn_brand_id || brand.id),
        run: runPdp,
        running: !!brand.pdp_report_running || pdpRunning,
        runTitle: "Run Brand PDP Analysis: audit this brand's product pages and roll the findings into one report",
      })}
      {line({
        label: 'CV',
        score:
          typeof brand.cv_score === 'number' ? (
            <CvScoreChip score={brand.cv_score} title="Consumer Voice Score: mean of this brand's products' latest analyses" />
          ) : null,
        latest: brand.cv_latest_report,
        view: () => setViewingCv(brand.cv_latest_report!.id),
        download: () => downloadConsumerVoicePdf(adminBrandConsumerVoiceItemEndpoint(brand.id, brand.cv_latest_report!.id)),
        run: runCv,
        running: !!brand.cv_running || cvRunning,
        runTitle: "Run Brand CV Analysis: read what shoppers say on Reddit about this brand's products",
      })}
      {line({
        label: 'AIP',
        score:
          typeof brand.aip_score === 'number' ? (
            <AipScoreChip score={brand.aip_score} title="AI Presence Score: mean of this brand's products' latest analyses" />
          ) : null,
        latest: brand.aip_latest_report,
        view: () => setViewingAip(brand.aip_latest_report!.id),
        download: () => downloadAiPresencePdf(adminBrandAiPresenceItemEndpoint(brand.id, brand.aip_latest_report!.id)),
        run: () => setPicker('aip'),
        running: !!brand.aip_running || aipRunning,
        runTitle: "Run Brand AI Presence Analysis: how often Claude, Gemini and OpenAI recommend this brand's products",
      })}
      {line({
        label: 'BI',
        status: biStatus,
        latest: brand.latest_prep_pack,
        view: null,
        download: () => downloadPrepPackById(salesPrepPackEndpoint(brand.latest_prep_pack!.id), { internal: true }),
        canDownload: brand.latest_prep_pack?.status === 'ready',
        run: runBi,
        running: !!brand.prep_pack_running || biRunning,
        runTitle: 'Run the Sales Intel Prep Pack: brand intelligence, PDP and Consumer Voice analysis from the catalog — no form',
      })}
      {viewingPdp !== null && (
        <ReportViewerDialog reportId={viewingPdp} rmnBrandId={brand.latest_pdp_report?.rmn_brand_id || brand.id} onClose={() => setViewingPdp(null)} />
      )}
      {viewingCv !== null && (
        <ConsumerVoiceReportDialog
          endpoint={adminBrandConsumerVoiceItemEndpoint(brand.id, viewingCv)}
          productEndpoint={(id) => adminBrandConsumerVoiceItemEndpoint(brand.id, id)}
          onClose={() => setViewingCv(null)}
          onCreateStrategy={(audit) => createStrategy('cv', audit)}
        />
      )}
      {viewingAip !== null && (
        <AiPresenceReportDialog
          endpoint={adminBrandAiPresenceItemEndpoint(brand.id, viewingAip)}
          productEndpoint={(id) => adminBrandAiPresenceItemEndpoint(brand.id, id)}
          onClose={() => setViewingAip(null)}
          onCreateStrategy={(audit) => createStrategy('aip', audit)}
        />
      )}
      <Dialog open={picker !== null} onClose={() => setPicker(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {PICKER_TITLES[picker || 'pdp']} — {brand.name}
          <IconButton aria-label="Close" onClick={() => setPicker(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {picker === 'pdp' && (
            <BrandReportForm
              brandId={brand.id}
              brandName={brand.name}
              rmnBrandId={brand.id}
              emptyAction={addProduct}
              onCreated={() => {
                setPicker(null);
                setPdpRunning(true);
                onChanged?.();
              }}
            />
          )}
          {picker === 'bi' && (
            <BrandIntelligenceRunForm
              brandId={brand.id}
              brandName={brand.name}
              rmnBrandId={brand.id}
              runEndpoint={adminBrandPrepPacksEndpoint(brand.id)}
              emptyAction={addProduct}
              onStarted={() => {
                setPicker(null);
                setBiRunning(true);
                onChanged?.();
              }}
            />
          )}
          {picker === 'cv' && (
            <BrandCvRunForm
              brandName={brand.name}
              targetsEndpoint={adminBrandConsumerVoiceTargetsEndpoint(brand.id)}
              runEndpoint={adminBrandConsumerVoiceEndpoint(brand.id)}
              emptyAction={addProduct}
              onCreated={() => {
                setPicker(null);
                setCvRunning(true);
                onChanged?.();
              }}
            />
          )}
          {picker === 'aip' && (
            <BrandAipRunForm
              brandName={brand.name}
              targetsEndpoint={adminBrandAiPresenceTargetsEndpoint(brand.id)}
              runEndpoint={adminBrandAiPresenceEndpoint(brand.id)}
              emptyAction={addProduct}
              onCreated={() => {
                setPicker(null);
                setAipRunning(true);
                onChanged?.();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
